import * as jQuery from "jquery";
import { Subject } from "rxjs";

export type KeyEventType = {
    keyCode: number,
    event: 'up' | 'down'
};

export type KeyPressedEventType = {
    keys: KeyInputState
};

export class InputHandler {
    public keyState: KeyInputState;
    public mouseState: MouseInputState;

    public keyEvents: Subject<KeyEventType>;
    public keyPressedEvents: Subject<KeyPressedEventType>;

    private jQueryCanvasHandle: JQuery<HTMLCanvasElement>;

    private mouseMovedEvent: InputMouseEvent;
    private mouseAdapters: MouseEventAdapter[];

    private registeredKeys: {};
    private keyCombos: KeyCombo[];

    private inputActions: ActionList;

    constructor(public canvas: HTMLCanvasElement) {
        this.jQueryCanvasHandle = jQuery(canvas);
        this.keyState = {};
        this.mouseState = {};
        this.mouseState[MouseButtonEnum.Left] = false;
        this.mouseState[MouseButtonEnum.Middle] = false;
        this.mouseState[MouseButtonEnum.Right] = false;

        this.mouseMovedEvent = null;
        this.mouseAdapters = [];

        this.keyCombos = [];
        this.registeredKeys = {};
        this.keyEvents = new Subject<KeyEventType>();
        this.keyPressedEvents = new Subject<KeyPressedEventType>();
        this.inputActions = {};
    }

    public setInputActions(inputActions: ActionList) {
        this.inputActions = inputActions;
    }

    private clearInputState() {
        this.mouseState = {};
        this.keyState = {};
    }

    public hookEvents() {

        this.jQueryCanvasHandle
            .keydown((e) => {
                this.keyDown(e as any);
            })
            .keyup((e) => {
                this.keyUp(e as any);
            });

        this.jQueryCanvasHandle
            .mousedown((e) => {
                this.mouseDown(this.convertMouseEvent(e as any));
            })
            .mouseup((e) => {
                this.mouseUp(this.convertMouseEvent(e as any));
            })
            .mousemove((e) => {
                // Force focus of the canvas when user moves mouse over it. This
                // prevents problems with keypresses being handled else where and
                // avoids the need to click the canvas to get focus.
                jQuery(e.currentTarget).focus();
                this.mouseMove(this.convertMouseEvent(e as any));
            })
            .mouseout((e) => {
                // When mouse leaves the canvas clear the input state and blur.
                jQuery(e.currentTarget).blur();
                this.clearInputState();
            });
    }

    public bindKeyMapping(keymap: KeyMap) {
        for (let i in keymap) {
            this.bindKeyCombo(keymap[i], this.inputActions[i]);
        }
    }

    public bindKeyCombo(keySet: KeySet, callback: (combo: KeyCombo, handler: InputHandler) => void) {
        if (keySet.length > 0) {
            for (let keys of keySet) {

                this.keyCombos.push({
                    keys: keys,
                    callback: callback
                });

                for (let key of keys) {
                    this.registeredKeys[key] = true;
                }
            }
        } else {
            throw new Error('Key combination must include atleast one key');
        }
    }

    public handleKeyboard() {

        let set = new Set();

        for (let combo of this.keyCombos) {
            let run = true;

            for (let key of combo.keys) {
                if (!this.keyState[key]) {
                    run = false;
                    break;
                }
            }

            if (run) {
                if (combo.callback && !set.has(combo.callback)) {
                    combo.callback(combo, this);
                    set.add(combo.callback);
                } else {
                    console.error('Triggered a key combination but no action is associated with it', combo);
                }
            }
        }
        for (let i in this.keyState) {
            if (this.keyState[i]) {
                this.keyPressedEvents.next({ keys: this.keyState });
                break;
            }
        }
    }

    public keyUp(e: KeyboardEvent) {
        this.keyState[e.keyCode] = false;

        this.keyEvents.next({ event: 'up', keyCode: e.keyCode });
    }

    public keyDown(e: KeyboardEvent) {
        this.keyState[e.keyCode] = true;
        if (this.registeredKeys[e.keyCode]) {
            e.preventDefault();
        }

        this.keyEvents.next({ event: 'down', keyCode: e.keyCode });
    }

    private mouseDown(e: InputMouseEvent) {
        if (e.button) {
            this.mouseState[e.button] = true;

            for (let adapter of this.mouseAdapters) {
                if (adapter.mouseDownEvent) {
                    adapter.mouseDownEvent(e);
                }
            }
        }
    }

    private mouseUp(e: InputMouseEvent) {
        if (e.button) {
            for (let adapter of this.mouseAdapters) {
                if (adapter.mouseUpEvent) {
                    adapter.mouseUpEvent(e);
                }
            }

            this.mouseState[e.button] = false;
        }
    }

    private mouseMove(e: InputMouseEvent) {
        this.mouseMovedEvent = e; // Store move event for next render pass
    }

    public handleMouse() {
        if (this.mouseMovedEvent) {
            for (let adapter of this.mouseAdapters) {
                if (adapter.mouseMoveEvent) {
                    adapter.mouseMoveEvent(this.mouseMovedEvent);
                }
            }

            this.mouseMovedEvent = null;
        }
    }

    public bindMouseEventAdapter(adapter: MouseEventAdapter) {
        this.mouseAdapters.push(adapter);
    }

    private convertMouseEvent(e: MouseEvent): InputMouseEvent {
        let parentOffset = this.jQueryCanvasHandle.offset();

        let button: MouseButtonEnum = null;
        switch (e.which) {
            case 1: button = MouseButtonEnum.Left; break;
            case 2: button = MouseButtonEnum.Middle; break;
            case 3: button = MouseButtonEnum.Right; break;
        }

        let relX = e.pageX - parentOffset.left;
        let relY = e.pageY - parentOffset.top;
        relY = this.jQueryCanvasHandle.height() - relY;

        let anyPressed = () => {
            for (var i in this.mouseState) {
                if (this.mouseState[i]) {
                    return true;
                }
            }
            return false;
        };

        return {
            position: { x: relX, y: relY },
            button: button,
            state: this.mouseState,
            anyPressed: anyPressed
        };
    }
}

type KeyMap = {
    [key: string]: KeySet
};

type ActionList = {
    [key: string]: () => void
}

type KeySet = [
    number[]
]

type KeyCombo = {
    keys: number[],
    callback: (combo: KeyCombo, handler: InputHandler) => void
}

type MouseEventAdapter = {
    mouseDownEvent?: (e: InputMouseEvent) => void,
    mouseUpEvent?: (e: InputMouseEvent) => void,
    mouseMoveEvent?: (e: InputMouseEvent) => void
}

type KeyInputState = {
    [key: number]: boolean
}

type MouseInputState = {
    [key: string]: boolean
}

export type InputMouseEvent = {
    position: {
        x: number,
        y: number
    },
    button: MouseButtonEnum,
    state: MouseInputState,
    anyPressed: () => boolean
}

export enum MouseButtonEnum {
    Left = 'left',
    Middle = 'middle',
    Right = 'right'
}

